// @flow
import createMaybeTransform from 'app/common/react/components/closeup/utils/createMaybeTransform';
import getClientTrackingParams from 'app/packages/client-tracking-params';
import iso8601Duration from 'app/common/lib/iso8601Duration';
import store from 'app/common/redux/store/singleton';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { CloseupPin } from 'app/packages/pinterest-flow-types/pin';
import type { ToMaybeHasPin } from 'app/common/react/components/closeup/utils/hasPin';
import { type Video } from 'app/packages/pinterest-flow-types/video';
import type {
  HasPin,
  HasRecipe,
  HasRichMetadata,
  MaybeAToMaybeAB,
  TimeInfo,
} from 'app/common/react/components/closeup/utils/types';

// Data Types
export type Product = {
  name: string,
  outOfStock: boolean,
  priceText: ?string,
};
export type BuyableProduct = {
  name: string,
};
export type PinImage = {
  height: number,
  url: string,
  width: number,
};

// Validated data types
export type HasRichSummary = {
  richSummary: Object,
};
export type HasTitle = {
  title: string,
};
export type HasDisplayName = {
  displayName: string,
};
export type VideoDetails = {|
  hasVideo: Video,
  hls?: Video,
  hlsv3?: Video,
  id: string,
  mp4?: Video,
|};
export type HasEmbed = {
  embed: {
    src: string,
    subtype?: string,
    type?: string,
  },
};
export type HasAttribution = {
  attribution: {
    authorName?: string,
    authorUrl?: string,
    ccUrl?: string,
    embed: {
      src: string,
    },
    providerName: string,
    providerUrl: string,
    title?: string,
  },
};
export type HasProducts = {
  products: {
    first: Product,
    rest: Array<Product>,
  },
};
export type HasBuyableProduct = {
  buyableProduct: BuyableProduct,
};
export type HasSiteName = {
  siteName: string,
};
export type HasMetaInfo = {
  metaInfo: {
    id: string,
    image: string,
  },
};
export type HasImages = {
  images: { [key: string]: PinImage },
};
export type HasImageOrig = {
  imageOrig: PinImage,
};
// TODO: Fill this out properly
export type HasPlace = {
  place: Object,
};

// Adds required tracking_params for Closeup Pin Views
export const clientTrackingParams = (id: string) => {
  const instance = store.instance;
  let clientTrackingParams;
  if (instance) {
    const state = instance.getState();
    if (state.pins && id) {
      const pin = state.pins[id];
      const location = state.location;
      if (pin) {
        clientTrackingParams = getClientTrackingParams(pin, location);
      }
    }
  }
  return clientTrackingParams;
};

/**
 * Helper method to create the closeup Pin resource options. Note: these options
 * are used in closeup resource invalidate calls so any changes to resource
 * options must be made here to prevent breaking other parts of the site
 * @param  {string} pinId The id of the Pin being shown in closeup
 * @return {Object}       The options sent to the resource fetch and invalidate
 */
export const closeupPinResourceOptions = (pinId: string, isFullNode: boolean = false) => ({
  id: pinId,
  // TODO (vmoy): clean up fields in 'auth_web_main_pin' fieldset, remove anything not needed for auth closeup
  field_set_key: 'auth_web_main_pin',
  // TODO (ablair): Include this once pure react is shipped so we can properly SSR pin pages
  // is_pure_react: true,
  is_landing_page: false,
  client_tracking_params: clientTrackingParams(pinId),
  allowStale: false,
  ...(isFullNode ? { force_node: true } : {}),
});

export const getViewParameter = (maybePin: ?ToMaybeHasPin) => {
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  if (maybePin && maybePin.pin) {
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    const richMetadata = maybePin.pin.rich_metadata;

    if (richMetadata) {
      if (richMetadata.products) {
        return PCONST.thrift.ViewParameterType.PIN_PRODUCT;
      } else if (richMetadata.article) {
        return PCONST.thrift.ViewParameterType.PIN_ARTICLE;
      } else if (richMetadata.recipe) {
        return PCONST.thrift.ViewParameterType.PIN_RECIPE;
      }
      // TODO (tuanhuynh): PCONST.thrift.ViewParameterType.PIN_PLACE

      return PCONST.thrift.ViewParameterType.PIN_OTHER;
    }

    if (!!maybePin.pin.story_pin_data) {
      return PCONST.thrift.ViewParameterType.PIN_STORY_PIN;
    }
  }
  return PCONST.thrift.ViewParameterType.PIN_REGULAR;
};

// #### NATIVE PIN TITLE ####
export type ToMaybeHasTitle = MaybeAToMaybeAB<HasPin, HasTitle>;
export const toMaybeHasTitle: ToMaybeHasTitle = createMaybeTransform((props) => {
  const { pin, inChangeTitlesExp } = props;
  const { carousel_data, title, closeup_unified_title } = pin;

  let finalTitle = inChangeTitlesExp ? closeup_unified_title : title;
  if (carousel_data) {
    const { carousel_slots = [], index } = carousel_data;
    if (carousel_slots[index] && carousel_slots[index].title) {
      finalTitle = carousel_slots[index].title;
    }
  }

  return finalTitle ? { ...props, title: finalTitle } : null;
});

// #### RICH METADATA ####
export type ToMaybeHasRichMetadata = MaybeAToMaybeAB<HasPin, HasRichMetadata>;
export const toMaybeHasRichMetadata: ToMaybeHasRichMetadata = createMaybeTransform((props) => {
  const { rich_metadata } = props.pin;
  return rich_metadata ? { ...props, richMetadata: rich_metadata } : null;
});

export type ToMaybeHasRichTitle = MaybeAToMaybeAB<HasRichMetadata, HasTitle>;
export const toMaybeHasRichTitle: ToMaybeHasRichTitle = createMaybeTransform(
  (props: HasRichMetadata) => {
    const { title } = props.richMetadata;
    return title ? { ...props, title } : null;
  },
);

// #### RICH SUMMARY ####
export type ToMaybeHasRichSummary = MaybeAToMaybeAB<HasPin, HasRichSummary>;
export const toMaybeHasRichSummary: ToMaybeHasRichSummary = createMaybeTransform((props) => {
  const { rich_summary } = props.pin;
  return rich_summary ? { ...props, richSummary: rich_summary } : null;
});

export type ToMaybeHasDisplayName = MaybeAToMaybeAB<HasRichSummary, HasDisplayName>;
export const toMaybeHasDisplayName: ToMaybeHasDisplayName = createMaybeTransform(
  (props: HasRichSummary) => {
    const { display_name } = props.richSummary;
    return display_name ? { ...props, displayName: display_name } : null;
  },
);

// #### ATTRIBUTION ####
export type ToMaybeHasAttribution = MaybeAToMaybeAB<HasPin, HasAttribution>;

export const toMaybeHasAttribution: ToMaybeHasAttribution = createMaybeTransform((props) => {
  const { attribution } = props.pin,
    {
      author_url: authorUrl,
      url: providerUrl,
      author_name: authorName,
      title,
      provider_name: providerName,
      cc_url: ccUrl,
      embed,
    } = attribution || {};
  return providerName && providerUrl
    ? {
        ...props,
        attribution: {
          providerUrl,
          providerName,
          title,
          authorUrl,
          authorName,
          ccUrl,
          embed,
        },
      }
    : null;
});

// #### PRODUCTS ####
export type ToMaybeHasProducts = MaybeAToMaybeAB<HasPin & HasRichMetadata, HasProducts>;

export const toMaybeHasProducts: ToMaybeHasProducts = createMaybeTransform((props) => {
  const { pin, richMetadata } = props,
    { buyable_availability } = pin,
    { products } = richMetadata,
    validProducts = Array.isArray(products)
      ? products
          .map((product) => {
            const { name, offer_summary } = product,
              { in_stock, min_price, max_price, price } = offer_summary || {};

            let priceText = null;
            if (min_price !== undefined && max_price !== undefined) {
              priceText = `${min_price} - ${max_price}`;
            } else if (price !== undefined) {
              priceText = `${price}`;
            }

            return name
              ? {
                  name,
                  outOfStock: buyable_availability === false || in_stock === false,
                  priceText,
                }
              : null;
          })
          .filter((product) => product !== null)
      : [];
  return validProducts.length > 0
    ? { ...props, products: { first: validProducts[0], rest: validProducts.slice(1) } }
    : null;
});

// #### BUYABLE PRODUCT ####
// TODO: Buyable products has been sunsetted. Should this function should be removed
export type ToMaybeHasBuyableProduct = MaybeAToMaybeAB<HasPin, HasBuyableProduct>;

export const toMaybeHasBuyableProduct: ToMaybeHasBuyableProduct = createMaybeTransform((props) => {
  const { buyable_product } = props.pin;
  const { title, items } = buyable_product || {};
  return title && items ? { ...props, buyableProduct: { name: title } } : null;
});

// #### RELATED PRODUCTS ####
export const eligibleForRelatedProducts = (pin: CloseupPin, viewer: AuthViewer): boolean =>
  !pin.is_promoted && viewer.country === 'US';

// #### RECIPE ####
const getTimeInfo: (timeInfo: Object) => TimeInfo | null = (timeInfo) => {
  const { h, m } = timeInfo || {},
    time = { hours: h || 0, minutes: m || 0 };
  return !(h === undefined && m === undefined)
    ? { h, m, time, isoDuration: iso8601Duration(time) }
    : null;
};

export type ToMaybeHasRecipe = MaybeAToMaybeAB<HasRichMetadata, HasRecipe>;

export const toMaybeHasRecipe: ToMaybeHasRecipe = createMaybeTransform((props) => {
  const { richMetadata } = props,
    { recipe } = richMetadata,
    {
      name,
      categorized_ingredients: categorizedIngredients,
      diets,
      from_aggregated_data: fromAggregatedData,
      cook_times,
      servings_summary,
    } = recipe || {},
    { total, cook, prep } = cook_times || {},
    { summary: servingSummary } = servings_summary || {},
    prepTime = getTimeInfo(prep),
    cookTime = getTimeInfo(cook),
    totalTime = getTimeInfo(total);
  return name
    ? {
        ...props,
        recipe: {
          name,
          categorizedIngredients,
          diets,
          fromAggregatedData,
          prepTime,
          cookTime,
          totalTime,
          servingSummary,
        },
      }
    : null;
});
